import { createContext, useContext, useState, useCallback } from "react";

type ACHPassthroughImportContextType = {
  validateResponse: API.ACHPassthrough.ValidateResponse | null;
  fileName: string | null;
  title: string;
  internalNote: string;
  submittedState: API.NachaPassthrough.SubmissionState | null;
  setValidateResponse: (
    data: API.ACHPassthrough.ValidateResponse,
    fileName: string
  ) => void;
  setTitle: (title: string) => void;
  setInternalNote: (internalNote: string) => void;
  setSubmittedState: (state: API.NachaPassthrough.SubmissionState) => void;
  clearValidation: () => void;
};

const ACHPassthroughImportContext =
  createContext<ACHPassthroughImportContextType>({
    validateResponse: null,
    fileName: null,
    title: "",
    internalNote: "",
    submittedState: null,
    setValidateResponse: () => {},
    setTitle: () => {},
    setInternalNote: () => {},
    setSubmittedState: () => {},
    clearValidation: () => {},
  });

export const ACHPassthroughImportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [validateResponse, setValidateResponseState] =
    useState<API.ACHPassthrough.ValidateResponse | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [submittedState, setSubmittedState] =
    useState<API.NachaPassthrough.SubmissionState | null>(null);

  const setValidateResponse = useCallback(
    (data: API.ACHPassthrough.ValidateResponse, name: string) => {
      setValidateResponseState(data);
      setFileName(name);
      setTitle(data.submission?.title ?? "");
      setInternalNote(data.submission?.internal_note ?? "");
    },
    []
  );

  const clearValidation = useCallback(() => {
    setValidateResponseState(null);
    setFileName(null);
    setTitle("");
    setInternalNote("");
    setSubmittedState(null);
  }, []);

  return (
    <ACHPassthroughImportContext.Provider
      value={{
        validateResponse,
        fileName,
        title,
        internalNote,
        submittedState,
        setValidateResponse,
        setTitle,
        setInternalNote,
        setSubmittedState,
        clearValidation,
      }}
    >
      {children}
    </ACHPassthroughImportContext.Provider>
  );
};

export const useACHPassthroughImportContext = () =>
  useContext(ACHPassthroughImportContext);

export default ACHPassthroughImportContext;
